// Presentation-only telemetry. Never retain financial inputs, results or file bodies.
const clock = () => performance.now();
const terminal = status => ['complete', 'cancelled', 'error'].includes(status);
export const swrJobPriority = type => type === 'run-swr-simulation' ? 0 : type.startsWith('generate-') ? 1 : 2;
const median = values => { const a = [...values].sort((x, y) => x - y); return a[Math.floor(a.length / 2)]; };

export function describeSWRActivity(type, params = {}, options = {}) {
  const portfolio = params.historicalPortfolio;
  const assets = portfolio?.allocations?.filter(a => a.weight > 0).length || 1;
  const count = type === 'run-swr-safe-withdrawal' ? options.numberOfSimulations || (options.mode === 'detailed' ? 5000 : 500)
    : type === 'run-swr-stress-tests' || (type.startsWith('generate-') && options.kind === 'stress') ? 3000 : params.numberOfSimulations || 3000;
  const labels = {
    'run-swr-simulation': 'Simulazione principale',
    'run-swr-safe-withdrawal': options.mode === 'detailed' ? 'Prelievo sostenibile · dettagliato' : 'Prelievo sostenibile · rapido',
    'run-swr-stress-tests': 'Stress test',
  };
  const isExport = type.startsWith('generate-');
  const signature = JSON.stringify([type, params.engineModelVersion || 'current', portfolio?.methodVersion || 'manual',
    assets, Boolean(params.dynamicCashBufferEnabled), params.withdrawalPolicy || 'fixed', params.guardrailsEnabled === true,
    params.withdrawalStrategy?.id || 'personalized', portfolio?.taxation?.salesPolicy || '',
    params.macroMode || 'fixed', options.mode || '', options.kind || '', options.format || '',
    options.kind === 'all' ? count : '',
    type === 'run-swr-safe-withdrawal' ? (options.validationNumberOfSimulations ?? (options.mode === 'detailed' ? 10000 : count)) / count : '']);
  return { type, label: labels[type] || (isExport ? `${options.format === 'xlsx' ? 'Excel' : 'CSV'} · ${options.kind === 'all' ? 'analisi completa' : options.kind === 'stress' ? 'stress test' : 'simulazioni'}` : 'Calcolo SWR'),
    kind: isExport ? 'export' : type === 'run-swr-safe-withdrawal' ? 'cards' : type === 'run-swr-stress-tests' ? 'stress' : 'main',
    signature, units: Math.max(1, count * (params.yearsToSimulate || 30) * assets) };
}

function phaseKey(job, text = '') {
  if (job.kind === 'cards') return /verifica|validaz/i.test(text) ? 'validation' : 'search';
  // Their percentages already cover complete paths/scenarios. Keep the rate
  // across repeated generation/save messages at the same percentage.
  if (job.kind === 'export') return 'export-parts';
  if (job.kind === 'stress') return 'stress-scenarios';
  return 'simulation';
}

// Forecast the existing two-slot priority queue, including not-yet-ready dependencies.
// Infinity means unknown, never zero duration. Jobs ready now precede future work.
export function forecastSWRQueue(jobs, remaining, concurrency = 2) {
  const finishes = new Map(), waits = new Map(), slots = Array(concurrency).fill(0);
  let active = 0;
  for (const job of jobs) {
    if (terminal(job.status)) { finishes.set(job.id, 0); continue; }
    if (['running', 'preparing', 'finalizing', 'cancelling'].includes(job.status)) {
      const duration = remaining.get(job.id) ?? Infinity;
      finishes.set(job.id, duration);
      if (job.status !== 'preparing' && job.workerActive !== false) slots[active++ % concurrency] = duration;
    }
  }
  const pending = jobs.filter(j => ['queued', 'planned'].includes(j.status)).sort((a, b) => swrJobPriority(a.type) - swrJobPriority(b.type) || a.order - b.order);
  while (pending.length) {
    const index = slots.indexOf(Math.min(...slots));
    let start = slots[index];
    const readyAt = job => job.dependsOn ? finishes.get(job.dependsOn) ?? Infinity : 0;
    let chosen = pending.findIndex(job => readyAt(job) <= start && Number.isFinite(readyAt(job)));
    if (chosen < 0) {
      start = Math.max(start, Math.min(...pending.map(readyAt)));
      chosen = pending.findIndex(job => readyAt(job) <= start && Number.isFinite(readyAt(job)));
    }
    if (chosen < 0 || !Number.isFinite(start)) break;
    const [job] = pending.splice(chosen, 1);
    const finish = start + (remaining.get(job.id) ?? Infinity);
    waits.set(job.id, start); finishes.set(job.id, finish); slots[index] = finish;
  }
  return { finishes, waits };
}

export function createSWRActivityMonitor({ now = clock } = {}) {
  let sequence = 0, runSequence = 0, notification = null;
  let hidden = false;
  const jobs = new Map(), history = new Map(), listeners = new Set();
  const publish = (immediate = false) => { notification = null; for (const listener of listeners) { try { listener(immediate); } catch (_) { /* Telemetry cannot fail a calculation. */ } } };
  const notify = immediate => {
    if (immediate) { clearTimeout(notification); publish(true); }
    else if (listeners.size && notification === null) notification = setTimeout(publish, 1000);
  };
  const getHistory = job => history.get(job.signature) || [];
  const duration = job => {
    const samples = getHistory(job);
    if (!samples.length) return null;
    const peers = samples.filter(s => s.concurrent === job.concurrent);
    if (!peers.length) return null;
    return median(peers.map(s => s.perUnit)) * job.units;
  };
  const create = (description, options = {}) => {
    // Keep active operations and their dependency graph; terminal history is bounded.
    const referenced = new Set([...jobs.values()].filter(j => !terminal(j.status)).map(j => j.dependsOn));
    for (const old of [...jobs.values()].filter(j => terminal(j.status) && (j.status !== 'error' || j.dismissed) && !referenced.has(j.id)).slice(0, -20)) jobs.delete(old.id);
    const id = options.id || `swr-activity-${++sequence}`;
    jobs.set(id, { ...description, id, order: ++sequence, context: options.context || '', dependsOn: options.dependsOn || null,
      status: options.status || 'planned', phase: '', percent: 0, createdAt: now(), startedAt: null,
      endedAt: null, workerActive: false, concurrent: 1, rate: null, samples: 0, phasePercent: 0, phaseAt: null, error: null, dismissed: false });
    notify(true); return id;
  };
  const update = (id, patch) => {
    const job = jobs.get(id);
    if (!job || terminal(job.status)) return;
    Object.assign(job, patch); notify(true);
  };
  const start = (id, concurrent = 1) => {
    const job = jobs.get(id); if (!job || terminal(job.status)) return;
    Object.assign(job, { status: 'running', workerActive: true, startedAt: now(), concurrent, phaseAt: now(), phasePercent: job.percent, suspended: hidden });
    // A change in contention invalidates rate and history matching, not the work.
    for (const other of jobs.values()) if (other.workerActive && other.id !== id) {
      other.mixedConcurrency = true; other.concurrent = concurrent; other.rate = null; other.samples = 0; other.workRate = null; other.workSamples = 0; other.workAt = now(); other.workCompleted = other.work?.completed; other.phaseAt = now(); other.phasePercent = other.percent;
    }
    notify(true);
  };
  const progress = (id, value) => {
    const job = jobs.get(id); if (!job || terminal(job.status)) return;
    const percent = typeof value === 'object' ? value?.percent : value;
    // Ignore stale events before they can reset a later phase or corrupt its rate.
    if (Number.isFinite(percent) && percent < job.percent) return;
    const phase = typeof value === 'object' ? value?.phase || job.phase : job.phase;
    const next = Math.min(99, Math.max(job.percent, Number.isFinite(percent) ? percent : job.percent));
    const work = typeof value === 'object' ? value?.work : null;
    if (work && ['search', 'validation'].includes(work.stage) && Number.isFinite(work.completed) && work.completed >= 0) {
      if (job.work?.stage !== work.stage) {
        job.workRate = null; job.workSamples = 0; job.workAt = now(); job.workCompleted = work.completed;
      }
      if (!hidden && work.completed > job.workCompleted && now() > job.workAt) {
        const observed = (now() - job.workAt) / (work.completed - job.workCompleted);
        job.workRate = job.workRate == null ? observed : .3 * observed + .7 * job.workRate;
        job.workSamples++; job.workAt = now(); job.workCompleted = work.completed;
      }
      job.work = { stage: work.stage, completed: work.completed,
        total: Number.isFinite(work.total) && work.total >= work.completed ? work.total : null,
        remainingStageUnits: Number.isFinite(work.remainingStageUnits) && work.remainingStageUnits >= 0 ? work.remainingStageUnits : 0 };
    }
    const key = phaseKey(job, phase);
    if (key !== job.phaseKey) {
      job.phaseKey = key; job.phasePercent = job.percent; job.phaseAt = now(); job.rate = null; job.samples = 0;
    }
    if (!hidden && job.phaseAt !== null && next > job.phasePercent && now() > job.phaseAt) {
      const observed = (now() - job.phaseAt) / (next - job.phasePercent);
      job.rate = job.rate === null ? observed : .3 * observed + .7 * job.rate;
      job.samples++; job.phaseAt = now(); job.phasePercent = next;
    }
    job.percent = next;
    if (job.phase !== phase) job.phaseMessage = describeText(phase);
    job.phase = phase; job.updatedAt = now(); notify(false);
  };
  const finish = (id, status, error) => {
    const job = jobs.get(id); if (!job || terminal(job.status)) return;
    job.status = status; job.workerActive = false; job.endedAt = now(); job.error = error || null;
    if (status === 'complete') {
      job.percent = 100;
      if (job.startedAt !== null && !job.suspended && !job.mixedConcurrency && job.endedAt > job.startedAt) {
        const samples = getHistory(job);
        history.set(job.signature, [...samples, { perUnit: (job.endedAt - job.startedAt) / job.units, concurrent: job.concurrent }].slice(-8));
        if (history.size > 60) history.delete(history.keys().next().value);
      }
    }
    const concurrent = [...jobs.values()].filter(j => j.workerActive).length;
    for (const other of jobs.values()) if (other.workerActive && other.concurrent !== concurrent) {
      other.mixedConcurrency = true; other.concurrent = Math.max(1, concurrent); other.rate = null; other.samples = 0; other.workRate = null; other.workSamples = 0; other.workAt = now(); other.workCompleted = other.work?.completed; other.phaseAt = now(); other.phasePercent = other.percent;
    }
    notify(true);
  };
  return {
    create, update, start, progress, finish,
    newRun: () => `Analisi ${++runSequence}`,
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); if (!listeners.size) { clearTimeout(notification); notification = null; } }; },
    dismiss(id) { const job = jobs.get(id); if (job && terminal(job.status)) { job.dismissed = true; notify(true); } },
    visibility(value) {
      if (hidden === value) return;
      hidden = value;
      for (const job of jobs.values()) if (!terminal(job.status)) {
        job.suspended = true; job.rate = null; job.samples = 0; job.workRate = null; job.workSamples = 0; job.workAt = now(); job.workCompleted = job.work?.completed; job.phaseAt = now(); job.phasePercent = job.percent;
        job.recalibrating = true;
      }
      notify(true);
    },
    snapshot() {
      const list = [...jobs.values()]; const remaining = new Map();
      for (const job of list) {
        let value = duration(job);
        if (job.startedAt !== null) {
          value = value === null ? null : Math.max(0, value - (now() - job.startedAt));
          if (!hidden && !job.work && job.samples >= 3 && now() - job.startedAt >= 1500 && job.percent >= 3 && job.rate !== null) {
            // Rates are reset at phase changes: validation never inherits search speed.
            // Unseen remaining phases use an indicative extrapolation, refined by session history.
            const observed = job.rate * (100 - job.percent);
            value = value === null ? observed : .7 * observed + .3 * value;
          }
          if (!hidden && job.work && job.workSamples >= 3 && now() - job.startedAt >= 1500
            && job.work.total !== null && job.workRate !== null) {
            const unitsLeft = job.work.total - job.work.completed + job.work.remainingStageUnits;
            // Same rate applies only to comparable path-evaluation units. Validation
            // recalibrates its own rate; percentages remain presentation-only.
            const observed = job.workRate * unitsLeft;
            value = value === null ? observed : .7 * observed + .3 * value;
          }
          if (job.status === 'finalizing' || hidden || now() - (job.updatedAt ?? job.startedAt) > 15000
            || (job.recalibrating && (job.work ? job.workSamples || 0 : job.samples) < 3) || (value !== null && value <= 0)) value = null;
        }
        remaining.set(job.id, value);
      }
      const forecast = forecastSWRQueue(list, remaining);
      return list.filter(j => !j.dismissed && (!terminal(j.status) || j.status === 'error' || now() - j.endedAt < 5000))
        .map(j => ({ id: j.id, type: j.type, kind: j.kind, label: j.label, context: j.context, status: j.status,
          order: j.order, percent: j.percent, phase: j.phase, phaseMessage: j.phaseMessage, error: j.error, dependsOn: j.dependsOn,
          recalibrating: hidden || (j.recalibrating && (j.work ? j.workSamples || 0 : j.samples) < 3) || (j.startedAt !== null && now() - (j.updatedAt ?? j.startedAt) > 15000),
          remainingMs: remaining.get(j.id), waitMs: Number.isFinite(forecast.waits.get(j.id)) ? forecast.waits.get(j.id) : null,
          finishMs: Number.isFinite(forecast.finishes.get(j.id)) ? forecast.finishes.get(j.id) : null }));
    },
  };
}

export const swrActivityMonitor = createSWRActivityMonitor();

export function beginSWRActivityPlan(params, options = {}) {
  const context = swrActivityMonitor.newRun();
  const main = swrActivityMonitor.create(describeSWRActivity('run-swr-simulation', params), { context });
  const cards = swrActivityMonitor.create(describeSWRActivity('run-swr-safe-withdrawal', params, options), { context, dependsOn: main });
  const stress = swrActivityMonitor.create(describeSWRActivity('run-swr-stress-tests', params), { context, dependsOn: cards });
  return { context, main, cards, stress };
}

export function cancelSWRActivityPlan(plan) {
  if (plan) for (const kind of ['main', 'cards', 'stress']) swrActivityMonitor.finish(plan[kind], 'cancelled');
}

export function formatSWRRemaining(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  const seconds = Math.max(1, Math.ceil(ms / 1000));
  if (seconds < 10) return 'pochi secondi';
  if (seconds < 60) return `${Math.ceil(seconds / 5) * 5} s`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} min`;
  const minutes = Math.ceil(seconds / 60);
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}
import { describeText } from '../i18n';
